import React, { useState, useEffect, useMemo } from "react";
import { Card, PageHeader, Button, Calendar, DateRangePicker, StatusBadge } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getStudentsThunk,
  getAttendanceThunk,
  createAttendanceThunk,
} from "../../../redux";
import { showSuccess } from "../../../services";
import { AttendanceStatus, AttendanceFieldRecord } from "../../../types";
import { parseYMD, formatYMD } from "../../../utils";
import calendarIcon from "../../../assets/icons/calendar.svg";
import "./TeacherAttendance.css";

const WEEK_DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TeacherAttendance: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { records } = useAppSelector((state) => state.student);
  const { records: attendanceHistory } = useAppSelector((state) => state.attendance);

  // Tab State
  const [activeTab, setActiveTab] = useState<"student" | "search">("student");

  // Picker Popover State
  const [openPicker, setOpenPicker] = useState<
    "attendanceDate" | "searchDateRange" | null
  >(null);

  // Marking State
  const todayStr = formatYMD(new Date());
  const [attendanceDate, setAttendanceDate] = useState<string>(todayStr);
  const fromTime = `${attendanceDate}T09:00:00+10:00`;
  const toTime = `${attendanceDate}T10:00:00+10:00`;
  const [remarks, setRemarks] = useState<string>("");
  const [presentMap, setPresentMap] = useState<Record<string, boolean>>({});

  // Batch Day State
  const availableBatchDays = useMemo(
    () =>
      Array.from(new Set(records.map((r) => r.Batch_Day).filter((d): d is string => Boolean(d)))).sort(
        (a, b) => WEEK_DAY_ORDER.indexOf(a) - WEEK_DAY_ORDER.indexOf(b)
      ),
    [records]
  );
  const [selectedBatchDay, setSelectedBatchDay] = useState<string>("");

  useEffect(() => {
    if (availableBatchDays.length > 0 && !availableBatchDays.includes(selectedBatchDay)) {
      setSelectedBatchDay(availableBatchDays[0]);
    }
  }, [availableBatchDays, selectedBatchDay]);

  const dayStudents = records.filter((student) => student.Batch_Day === selectedBatchDay);

  // Search Attendance State
  const [searchStartDate, setSearchStartDate] = useState<string>(todayStr);
  const [searchEndDate, setSearchEndDate] = useState<string>(todayStr);

  const handleSearchAttendance = (start = searchStartDate, end = searchEndDate, force = true) => {
    if (!user?.id) return;

    const selectQuery = `select id, Name, Teacher, Student, Date, Attendance_Status, From_Time, To_Time from Attendance where Teacher = '${user.id}' and Date between '${start}' and '${end}' order by Date desc limit 200`;

    dispatch(getAttendanceThunk({ query: selectQuery, force }));
  };

  useEffect(() => {
    if (user?.id) {
      dispatch(getStudentsThunk(user.id));
      handleSearchAttendance(todayStr, todayStr, false);
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    const initialPresent: Record<string, boolean> = {};
    records.forEach((student) => {
      initialPresent[student.id] = false;
    });
    setPresentMap(initialPresent);
  }, [records]);

  // Outside click listener for calendar popovers
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".date-picker-wrapper")) {
        setOpenPicker(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSelectDate = (
    key: "attendanceDate",
    value: Date
  ) => {
    const formatted = formatYMD(value);
    if (key === "attendanceDate") {
      setAttendanceDate(formatted);
    }
    setOpenPicker(null);
  };

  const handleMarkAllPresent = () => {
    setPresentMap((prev) => {
      const updated = { ...prev };
      dayStudents.forEach((student) => {
        updated[student.id] = true;
      });
      return updated;
    });
  };

  const handleTogglePresent = (studentId: string, checked: boolean) => {
    setPresentMap((prev) => ({
      ...prev,
      [studentId]: checked,
    }));
  };

  const handleSubmit = () => {
    if (!user?.id || dayStudents.length === 0) return;

    const presentStudents = dayStudents.filter(
      (student) => presentMap[student.id]
    );
    if (presentStudents.length === 0) return;

    const fields: AttendanceFieldRecord[] = presentStudents.map((student) => ({
      Name: `${[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")} - ${attendanceDate} - 9:00 AM`,
      Teacher: user.id,
      Student: student.id,
      Date: attendanceDate,
      Attendance_Status: "Present",
      From_Time: fromTime,
      To_Time: toTime,
      Remarks: remarks,
    }));

    dispatch(
      createAttendanceThunk({
        fields,
        trigger: ["workflow"],
      })
    ).then((res) => {
      if (createAttendanceThunk.fulfilled.match(res)) {
        showSuccess("Attendance saved successfully!");
        handleSearchAttendance();
      }
    });
  };

  // Extract unique session columns from attendanceHistory
  const sessionColumns: { key: string; date: string }[] = [];
  const sessionKeys = new Set<string>();

  attendanceHistory.forEach((item) => {
    const key = item.Date;
    if (!sessionKeys.has(key)) {
      sessionKeys.add(key);
      sessionColumns.push({
        key,
        date: item.Date,
      });
    }
  });

  // Map status by student ID and session date
  const historyStatusMap = new Map<string, AttendanceStatus>();
  attendanceHistory.forEach((item) => {
    if (item.Student?.id) {
      historyStatusMap.set(`${item.Student.id}_${item.Date}`, item.Attendance_Status);
    }
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card className="attendance-page-card attendance-header-card" style={{ padding: "24px 24px 0 24px" }}>
        <PageHeader
          title="Student Attendance"
          subtitle="Mark and search attendance for your allocated students."
        />
        <div className="attendance-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === "student" ? "active" : ""}`}
            onClick={() => setActiveTab("student")}
          >
            Student Attendance
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === "search" ? "active" : ""}`}
            onClick={() => setActiveTab("search")}
          >
            Search Attendance
          </button>
        </div>
      </Card>

      {activeTab === "student" && (
        <div className="attendance-split-container">
          <Card className="attendance-page-card attendance-details-side" style={{ padding: "20px" }}>
            <h3 style={{ margin: "0 0 16px 0" }}>Attendance Details</h3>
            <div className="attendance-form-vertical">
              <div className="attendance-field date-picker-wrapper">
                <label>Date</label>
                <div
                  className="input-with-icon"
                  onClick={() =>
                    setOpenPicker((prev) => (prev === "attendanceDate" ? null : "attendanceDate"))
                  }
                >
                  <input type="text" readOnly value={attendanceDate} className="date-input" />
                  <img src={calendarIcon} alt="Calendar" className="calendar-icon-img" />
                </div>
                {openPicker === "attendanceDate" && (
                  <div className="calendar-popover-dropdown">
                    <Calendar
                      value={parseYMD(attendanceDate)}
                      onChange={(val) => handleSelectDate("attendanceDate", val as Date)}
                    />
                  </div>
                )}
              </div>

              <div className="attendance-field">
                <label>Remarks</label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              <div className="submit-container-side">
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleSubmit}
                  disabled={dayStudents.length === 0}
                  style={{ width: "100%" }}
                >
                  Save Attendance
                </Button>
              </div>
            </div>
          </Card>

          <Card className="attendance-page-card attendance-table-side" style={{ padding: "20px" }}>
            <div className="attendance-list-header">
              <h3 style={{ margin: 0 }}>Student Attendance</h3>
              <div className="bulk-buttons">
                {availableBatchDays.length > 0 && (
                  <div className="batch-days-bar">
                    {availableBatchDays.map((day) => (
                      <button
                        key={day}
                        type="button"
                        className={`batch-day-tab ${selectedBatchDay === day ? "active" : ""}`}
                        onClick={() => setSelectedBatchDay(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleMarkAllPresent}
                  disabled={dayStudents.length === 0}
                >
                  Mark All Present
                </Button>
              </div>
            </div>

            <div className="student-attendance-table">
              {dayStudents.map((student) => {
                const isPresent = Boolean(presentMap[student.id]);

                return (
                  <div key={student.id} className="student-attendance-row">
                      <div className="student-info">
                        <span className="student-name">
                          {student.Student_Number2} - {[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")}
                        </span>
                      </div>

                      <div className="attendance-actions">
                        <label className="checkbox-option present-option">
                          <input
                            type="checkbox"
                            className="attendance-checkbox present-checkbox"
                            checked={isPresent}
                            onChange={(e) => handleTogglePresent(student.id, e.target.checked)}
                          />
                          <span>Present</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "search" && (
        <Card className="attendance-page-card" style={{ padding: "24px" }}>
          <h3 style={{ margin: "0 0 16px 0" }}>Search Attendance</h3>
          <div className="attendance-search-bar">
            <DateRangePicker
              label="Date Range"
              startDate={searchStartDate}
              endDate={searchEndDate}
              isOpen={openPicker === "searchDateRange"}
              onToggle={() =>
                setOpenPicker((prev) => (prev === "searchDateRange" ? null : "searchDateRange"))
              }
              onChange={(start, end) => {
                setSearchStartDate(start);
                setSearchEndDate(end);
                setOpenPicker(null);
              }}
            />

            <div className="attendance-field search-btn-container">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => handleSearchAttendance()}
              >
                Search Attendance
              </Button>
            </div>
          </div>

          {attendanceHistory.length === 0 ? (
            <div className="no-history-records">
              No attendance records found for the selected date range.
            </div>
          ) : (
            <div className="excel-matrix-container">
              <div className="excel-matrix-left">
                <div className="student-header-cell">
                  <span className="header-student-title">Student Name</span>
                </div>
                {records.map((student) => (
                  <div key={student.id} className="student-body-cell">
                    <span className="student-cell-name">
                      {student.Student_Number2} - {[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")}
                    </span>
                  </div>
                ))}
              </div>

              <div className="excel-matrix-right">
                <table
                  className="excel-matrix-table"
                  style={{
                    width: sessionColumns.length > 6 ? `${(sessionColumns.length / 6) * 100}%` : "100%",
                    minWidth: `${sessionColumns.length * 130}px`,
                  }}
                >
                  <thead>
                    <tr>
                      {sessionColumns.map((col) => (
                        <th key={`date-${col.key}`} className="date-header">
                          {col.date}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((student) => (
                      <tr key={student.id} className="matrix-row">
                        {sessionColumns.map((col) => {
                          const status = historyStatusMap.get(`${student.id}_${col.key}`);
                          return (
                            <td key={`${student.id}-${col.key}`} className="status-cell">
                              {status ? (
                                <StatusBadge variant={status === "Present" ? "success" : "error"}>
                                  {status}
                                </StatusBadge>
                              ) : (
                                <span className="no-status">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default TeacherAttendance;
